import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IncidentSeverity, ReportStatus } from '@dsr/shared-types';
import { useIncidents, useTransitionIncident } from '../api/queries/incidents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { fmtDateTime } from '../utils/date';
import {
  CATEGORY_LABEL,
  STATUS_LABEL,
  SeverityBadge,
  StatusBadge,
  firstFieldError,
} from './IncidentsPage';
import { Button, Select, Table, type SelectOption } from '../components/ui';
import './reports.css';

// Statuses that constitute the reviewer's queue: anything submitted by an
// author that hasn't yet reached a terminal (approved) state. Rejected items
// stay out — they're back with the author for revision.
const QUEUE_STATUSES: ReportStatus[] = ['submitted', 'under_review'];

const SEVERITY_RANK: Record<IncidentSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

export function IncidentReviewQueuePage(): JSX.Element {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'all'>('all');

  // The list endpoint takes a single status; when no sub-filter is chosen we
  // fetch both queue statuses and merge. Two stable query keys keep caching
  // clean.
  const submittedQuery = useIncidents({ status: 'submitted' });
  const underReviewQuery = useIncidents({ status: 'under_review' });
  const transition = useTransitionIncident();

  const isPending =
    (statusFilter === 'all' && (submittedQuery.isPending || underReviewQuery.isPending)) ||
    (statusFilter === 'submitted' && submittedQuery.isPending) ||
    (statusFilter === 'under_review' && underReviewQuery.isPending);

  const error = submittedQuery.error ?? underReviewQuery.error;

  if (!can('incident.review')) {
    return (
      <p className="rpt-status-line rpt-status-error">
        You don't have permission to review incidents.
      </p>
    );
  }

  if (isPending) return <p className="rpt-status-line">Loading review queue…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view incidents."
        : 'Failed to load the review queue.';
    return <p className="rpt-status-line rpt-status-error">{msg}</p>;
  }

  const submitted = submittedQuery.data?.incidents ?? [];
  const underReview = underReviewQuery.data?.incidents ?? [];

  let items =
    statusFilter === 'submitted'
      ? submitted
      : statusFilter === 'under_review'
        ? underReview
        : [...submitted, ...underReview];

  const stageFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All stages' },
    ...QUEUE_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];

  // Most urgent first: severity, then oldest report (so nothing ages out).
  items = [...items].sort((a, b) => {
    const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (sev !== 0) return sev;
    return (a.occurred_at ?? a.created_at) - (b.occurred_at ?? b.created_at);
  });

  const act = async (
    incidentId: number,
    status: ReportStatus,
    e: React.MouseEvent,
  ): Promise<void> => {
    e.stopPropagation();
    const note =
      status === 'rejected'
        ? window.prompt('Reason for rejection (optional):') ?? undefined
        : undefined;
    try {
      await transition.mutateAsync({
        id: incidentId,
        status,
        ...(note ? { note } : {}),
      });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (err) {
      toast.danger(firstFieldError(err, 'Could not change status.'));
    }
  };

  return (
    <section className="rpt-page">
      <header className="rpt-header">
        <div>
          <h2>Incident review queue</h2>
          <p className="rpt-sub">
            Submitted incidents awaiting review, sorted by severity. {items.length}{' '}
            pending.
          </p>
        </div>
        <div className="rpt-header-actions">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as ReportStatus | 'all')}
            options={stageFilterOptions}
            aria-label="Filter by stage"
          />
        </div>
      </header>

      <div className="rpt-card rpt-card-tight">
        {items.length === 0 ? (
          <p className="rpt-empty">The review queue is clear.</p>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Occurred</Table.HeaderCell>
                <Table.HeaderCell>Category</Table.HeaderCell>
                <Table.HeaderCell>Severity</Table.HeaderCell>
                <Table.HeaderCell>Stage</Table.HeaderCell>
                <Table.HeaderCell>Report #</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((inc) => (
                <Table.Row
                  key={inc.id}
                  interactive
                  onClick={() => navigate(`/incidents/${inc.id}`)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/incidents/${inc.id}`);
                  }}
                >
                  <Table.Cell tone="strong">{fmtDateTime(inc.occurred_at)}</Table.Cell>
                  <Table.Cell>{CATEGORY_LABEL[inc.category]}</Table.Cell>
                  <Table.Cell>
                    <SeverityBadge severity={inc.severity} />
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={inc.status} />
                  </Table.Cell>
                  <Table.Cell tone="mono">{inc.report_number ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <div className="rpt-inline-actions">
                      {inc.status === 'submitted' && (
                        <Button
                          variant="ghost"
                          onClick={(e) => act(inc.id, 'under_review', e)}
                          disabled={transition.isPending}
                        >
                          start review
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        onClick={(e) => act(inc.id, 'approved', e)}
                        disabled={transition.isPending}
                      >
                        approve
                      </Button>
                      <Button
                        variant="danger"
                        onClick={(e) => act(inc.id, 'rejected', e)}
                        disabled={transition.isPending}
                      >
                        reject
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </section>
  );
}
